// E-processos — igual à Veri, no padrão Simpler (EPROCESSOS_V1)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  RefreshCw, Loader2, ArrowUpDown, ArrowUp, ArrowDown, History, X, ListOrdered,
  FolderOpen, CheckCircle2, Clock3, Archive,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  trunc, cnpjFmt, dataBR, btn, Tip, Toolbar, Paginacao, useBusca, Cabecalho, Overlay, Buscando, Alerta,
} from "@/pages/comprovantes/ui";

const POR_PAGINA = 50;
const ROTA_BASE = "/app/dashboard-federal";

const tom = (sit) => {
  const s = String(sit || "").toUpperCase();
  if (!s) return "sem";
  if (s.includes("CONFIRMAD")) return "confirmado";
  if (s.includes("ARQUIVAD") || s.includes("ENCERRAD") || s.includes("FINALIZAD")) return "arquivado";
  if (s.includes("COBRAN") || s.includes("INDEFERID") || s.includes("CANCELAD")) return "atencao";
  return "andamento";
};
const BADGE = {
  confirmado: "border-success/20 bg-success/10 text-success",
  arquivado: "border-border bg-muted text-muted-foreground",
  andamento: "border-warning/20 bg-warning/10 text-warning",
  atencao: "border-destructive/20 bg-destructive/10 text-destructive",
  sem: "border-dashed border-border text-muted-foreground",
};
const Badge = ({ situacao }) => (
  <span className={`inline-flex min-w-[104px] items-center justify-center rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${BADGE[tom(situacao)]}`}>
    {situacao || "Sem processo"}
  </span>
);
const dataHora = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const erroMsg = (e) => e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || "Erro inesperado";

/* ---------------- Modal Histórico ---------------- */
function ModalHistorico({ empresa, onClose }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => {
    api.get(`/eprocessos/${empresa.cnpj}/historico`).then((r) => setDados(r.data)).catch((e) => setErro(erroMsg(e)));
  }, [empresa.cnpj]);
  const procs = dados?.processos || [];

  return (
    <Overlay onClose={onClose} z="z-[9990]">
      <div className="w-full max-w-[960px] rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-border p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
              <History className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Histórico de E-processos</p>
              <h2 className="text-base font-semibold leading-6 text-foreground">{empresa.razao_social}</h2>
              <p className="font-mono text-xs text-muted-foreground">{cnpjFmt(empresa.cnpj)}</p>
            </div>
          </div>
          <button onClick={onClose} className={btn.icon}><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ListOrdered className="h-4 w-4 text-primary" /> Todos os Processos
            {procs.length > 0 && <span className="font-normal text-muted-foreground">({procs.length})</span>}
          </div>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-warning" /> Processos ordenados por data mais recente
          </p>

          <div className="mt-4 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 text-left">Número do Processo</th>
                  <th className="px-3 py-3 text-left">Data Protocolo</th>
                  <th className="px-3 py-3 text-left">Tipo</th>
                  <th className="px-3 py-3 text-left">Subtipo</th>
                  <th className="px-3 py-3 text-center">Situação</th>
                  <th className="px-4 py-3 text-left">Localização</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {!dados && !erro && (
                  <tr><td colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</span>
                  </td></tr>
                )}
                {erro && <tr><td colSpan={6} className="py-10 text-center text-sm text-destructive">{erro}</td></tr>}
                {dados && procs.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    {dados.atualizado_em ? "Nenhum processo encontrado para esta empresa no e-Processo." : "Empresa ainda não consultada. Clique em Atualizar na lista."}
                  </td></tr>
                )}
                {procs.map((p) => (
                  <tr key={p.numero} className="transition-colors hover:bg-muted/30">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-foreground">{p.numero}</td>
                    <td className="px-3 py-3 tabular-nums text-muted-foreground">{dataBR(p.data_protocolo) || "—"}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{p.tipo || "—"}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{p.subtipo || "—"}</td>
                    <td className="px-3 py-3 text-center"><Badge situacao={p.situacao} /></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{p.localizacao || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          <span className="text-[11px] text-muted-foreground">
            {dados?.atualizado_em ? `Consultado em ${dataHora(dados.atualizado_em)}` : ""}
            {dados?.erro && <span className="ml-2 text-destructive">· Última tentativa falhou: {dados.erro}</span>}
          </span>
          <button onClick={onClose} className={btn.outline}><X className="h-4 w-4" /> Fechar</button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------------- Página ---------------- */
export default function EProcessosPage() {
  const navigate = useNavigate();
  const qs = new URLSearchParams(useLocation().search);
  const [dados, setDados] = useState({ registros: [], total_empresas: 0, consultadas: 0, lote: {} });
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [ordem, setOrdem] = useState({ key: "razao_social", asc: true });
  const [visiveis, setVisiveis] = useState({ numero: false, tipo: false, localizacao: false, total: false, atualizado_em: false });
  const [filtro, setFiltro] = useState(qs.get("situacao") || "todos");
  const [periodo, setPeriodo] = useState("todos");
  const [historico, setHistorico] = useState(null);
  const [buscando, setBuscando] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const poll = useRef(null);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const { data } = await api.get("/eprocessos");
      setDados(data);
    } catch (e) {
      if (!silencioso) setAlerta({ titulo: "Não foi possível carregar os E-processos", msg: erroMsg(e) });
    } finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => setPagina(1), [busca, filtro, periodo]);

  const loteAtivo = dados.lote?.status === "processando";
  useEffect(() => {
    clearInterval(poll.current);
    if (loteAtivo) poll.current = setInterval(() => carregar(true), 4000);
    return () => clearInterval(poll.current);
  }, [loteAtivo, carregar]);

  const atualizarLote = async () => {
    try {
      const { data } = await api.post("/eprocessos/atualizar-lote");
      setAlerta({
        icone: "info",
        titulo: "Em processamento",
        msg: data?.total
          ? "Estamos atualizando as informações e esse processo pode levar alguns minutos. Em breve suas empresas estarão atualizadas."
          : "Todas as empresas já foram consultadas nas últimas horas. Use o botão de atualizar da linha para forçar uma empresa específica.",
      });
      carregar(true);
    } catch (e) {
      setAlerta({ titulo: "Não foi possível iniciar a atualização", msg: erroMsg(e) });
    }
  };

  const atualizarUma = async (l) => {
    setBuscando("Atualizando informações.\nPor favor, aguarde alguns segundos...");
    try {
      const { data } = await api.post(`/eprocessos/${l.cnpj}/atualizar`);
      setDados((d) => ({ ...d, registros: d.registros.map((x) => (x.cnpj === l.cnpj ? data : x)) }));
    } catch (e) {
      setAlerta({ titulo: `Não foi possível atualizar ${trunc(l.razao_social, 40)}`, msg: erroMsg(e) });
    } finally { setBuscando(null); }
  };

  // linhas "achatadas" para busca/ordenação/exportação
  const todas = useMemo(() => (dados.registros || []).map((r) => ({
    ...r,
    data: r.ultimo?.data_protocolo || "",
    situacao: r.ultimo?.situacao || "",
    numero: r.ultimo?.numero || "",
    tipo: r.ultimo?.tipo || "",
    localizacao: r.ultimo?.localizacao || "",
    _tom: r.ultimo ? tom(r.ultimo.situacao) : "sem",
  })), [dados.registros]);

  const cont = useMemo(() => {
    const c = { confirmado: 0, andamento: 0, atencao: 0, arquivado: 0, sem: 0 };
    todas.forEach((r) => { c[r._tom] += 1; });
    return c;
  }, [todas]);

  const base = useMemo(() => {
    let b = todas;
    if (filtro === "todos") b = b.filter((r) => r.ultimo);
    else if (filtro === "andamento") b = b.filter((r) => r._tom === "andamento" || r._tom === "atencao");
    else b = b.filter((r) => r._tom === filtro);
    if (periodo !== "todos") {
      const d = new Date(); d.setDate(d.getDate() - Number(periodo));
      const lim = d.toISOString().slice(0, 10);
      b = b.filter((r) => r.data && r.data >= lim);
    }
    return b;
  }, [todas, filtro, periodo]);

  const filtradas = useBusca(base, ["razao_social", "cnpj", "situacao", "numero"], busca);
  const ordenadas = [...filtradas].sort((a, b) => {
    const va = a[ordem.key] ?? "", vb = b[ordem.key] ?? "";
    const r = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "pt-BR");
    return ordem.asc ? r : -r;
  });
  const paginaLinhas = ordenadas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const colunas = [
    { key: "razao_social", label: "Razão Social", fixa: true },
    { key: "cnpj", label: "CNPJ", exportar: (l) => cnpjFmt(l.cnpj) },
    { key: "data", label: "Data Processo Mais Recente", exportar: (l) => dataBR(l.data) },
    { key: "situacao", label: "Situação Processo Mais Recente", exportar: (l) => l.situacao || "Sem processo" },
    { key: "numero", label: "Nº Processo" },
    { key: "tipo", label: "Tipo" },
    { key: "localizacao", label: "Localização" },
    { key: "total", label: "Qtd. Processos" },
    { key: "atualizado_em", label: "Consultado em", exportar: (l) => dataHora(l.atualizado_em) },
  ];
  const vis = (k) => visiveis[k] !== false;
  const nCols = colunas.filter((c) => vis(c.key)).length + 2;

  const Ord = ({ k, label, align = "left" }) => {
    const ativo = ordem.key === k;
    const Icone = !ativo ? ArrowUpDown : ordem.asc ? ArrowUp : ArrowDown;
    return (
      <button onClick={() => setOrdem((o) => ({ key: k, asc: o.key === k ? !o.asc : true }))}
        className={`inline-flex items-center gap-1 uppercase hover:text-foreground ${ativo ? "text-foreground" : ""} ${align === "center" ? "justify-center" : ""}`}>
        {label} <Icone className={`h-3 w-3 ${ativo ? "text-primary" : "opacity-60"}`} />
      </button>
    );
  };

  const Resumo = ({ alvo, valor, label, desc, Icone, cor }) => {
    const ativo = filtro === alvo;
    return (
      <button onClick={() => setFiltro(ativo ? "todos" : alvo)}
        className={`rounded-2xl border bg-card p-4 text-left transition ${ativo ? "border-primary/50 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icone className={`h-4 w-4 ${cor}`} strokeWidth={1.8} />
        </div>
        <p className="mt-2 font-display text-2xl tabular-nums text-foreground">{carregando ? "—" : valor}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{desc}</p>
      </button>
    );
  };

  const lote = dados.lote || {};

  return (
    <div className="space-y-6">
      <Cabecalho titulo="E-processos"
        subtitulo="Processos digitais da Receita Federal (e-Processo) das empresas da carteira"
        trilha={[{ label: "Dashboard Federal", onClick: () => navigate(ROTA_BASE) }, { label: "E-processos" }]}>
        <Tip text="Clique aqui para o sistema atualizar todas as empresas" side="bottom">
          <button onClick={atualizarLote} disabled={loteAtivo} className={btn.primary}>
            {loteAtivo ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {loteAtivo ? "Atualizando..." : "Atualizar em Lote"}
          </button>
        </Tip>
      </Cabecalho>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Resumo alvo="confirmado" valor={cont.confirmado} label="Confirmados" desc="Processo recente confirmado" Icone={CheckCircle2} cor="text-success/70" />
        <Resumo alvo="andamento" valor={cont.andamento + cont.atencao} label="Em andamento" desc={cont.atencao ? `${cont.atencao} com cobrança/indeferimento` : "Em análise na RFB"} Icone={Clock3} cor="text-warning/80" />
        <Resumo alvo="arquivado" valor={cont.arquivado} label="Arquivados" desc="Último processo arquivado" Icone={Archive} cor="text-muted-foreground" />
        <Resumo alvo="sem" valor={cont.sem} label="Sem processo" desc="Nenhum processo no e-Processo" Icone={FolderOpen} cor="text-primary/60" />
      </section>

      <section className="rounded-2xl border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <Toolbar colunas={colunas} visiveis={visiveis} setVisiveis={setVisiveis} linhas={ordenadas}
            nome="e-processos" busca={busca} setBusca={setBusca}>
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}
              className="h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="todos">Qualquer data</option>
              <option value="30">Protocolo nos últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="365">Últimos 12 meses</option>
            </select>
            {filtro !== "todos" && (
              <button onClick={() => setFiltro("todos")} className={btn.ghost}><X className="h-4 w-4" /> Limpar filtro</button>
            )}
          </Toolbar>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {vis("razao_social") && <th className="px-5 py-3 text-left"><Ord k="razao_social" label="Razão Social" /></th>}
                {vis("cnpj") && <th className="px-3 py-3 text-left"><Ord k="cnpj" label="CNPJ" /></th>}
                {vis("data") && <th className="px-3 py-3 text-center"><Ord k="data" label="Data Processo Mais Recente" align="center" /></th>}
                {vis("situacao") && <th className="px-3 py-3 text-center"><Ord k="situacao" label="Situação Processo Mais Recente" align="center" /></th>}
                {vis("numero") && <th className="px-3 py-3 text-left"><Ord k="numero" label="Nº Processo" /></th>}
                {vis("tipo") && <th className="px-3 py-3 text-left"><Ord k="tipo" label="Tipo" /></th>}
                {vis("localizacao") && <th className="px-3 py-3 text-left"><Ord k="localizacao" label="Localização" /></th>}
                {vis("total") && <th className="px-3 py-3 text-center"><Ord k="total" label="Qtd." align="center" /></th>}
                {vis("atualizado_em") && <th className="px-3 py-3 text-left"><Ord k="atualizado_em" label="Consultado em" /></th>}
                <th className="w-24 px-3 py-3 text-center">Histórico</th>
                <th className="w-24 px-5 py-3 text-center">Atualizar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {carregando && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando empresas...</span>
                </td></tr>
              )}
              {!carregando && paginaLinhas.length === 0 && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">
                  {!dados.consultadas ? "Nenhuma empresa consultada ainda. Clique em Atualizar em Lote para buscar os processos na Receita Federal." : filtro === "todos" ? "Nenhuma empresa com processo no e-Processo." : "Nenhum registro para este filtro."}
                </td></tr>
              )}
              {!carregando && paginaLinhas.map((l) => (
                <tr key={l.cnpj} className="transition-colors hover:bg-muted/30">
                  {vis("razao_social") && <td className="px-5 py-3"><p className="font-medium text-foreground" title={l.razao_social}>{trunc(l.razao_social)}</p></td>}
                  {vis("cnpj") && <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{cnpjFmt(l.cnpj)}</td>}
                  {vis("data") && <td className="px-3 py-3 text-center tabular-nums text-muted-foreground">{dataBR(l.data) || "—"}</td>}
                  {vis("situacao") && (
                    <td className="px-3 py-3 text-center">
                      {l.consultado ? <Badge situacao={l.situacao} /> : (
                        <Tip text="Empresa ainda não consultada no e-Processo">
                          <span className="inline-flex min-w-[104px] items-center justify-center rounded-md border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground">Não consultada</span>
                        </Tip>
                      )}
                    </td>
                  )}
                  {vis("numero") && <td className="px-3 py-3 font-mono text-xs text-muted-foreground">{l.numero || "—"}</td>}
                  {vis("tipo") && <td className="px-3 py-3 text-xs text-muted-foreground">{l.tipo || "—"}</td>}
                  {vis("localizacao") && <td className="px-3 py-3 text-xs text-muted-foreground">{l.localizacao || "—"}</td>}
                  {vis("total") && <td className="px-3 py-3 text-center tabular-nums text-muted-foreground">{l.total}</td>}
                  {vis("atualizado_em") && <td className="px-3 py-3 text-xs text-muted-foreground">{dataHora(l.atualizado_em)}</td>}
                  <td className="px-3 py-3 text-center">
                    <Tip text="Clique para ver o histórico">
                      <button onClick={() => setHistorico(l)} className={`${btn.icon} text-primary hover:text-primary`}><History className="h-4 w-4" /></button>
                    </Tip>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <Tip text={l.erro ? `Última tentativa falhou: ${l.erro}\nClique para tentar de novo` : "Clique para atualizar informação"}>
                      <button onClick={() => atualizarUma(l)} disabled={!!buscando}
                        className={`${btn.icon} ${l.erro ? "text-destructive hover:text-destructive" : "text-success hover:text-success"}`}>
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </Tip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao pagina={pagina} setPagina={setPagina} total={ordenadas.length} porPagina={POR_PAGINA} />
      </section>

      {historico && <ModalHistorico empresa={historico} onClose={() => setHistorico(null)} />}
      {buscando && <Buscando msg={buscando} />}
      {alerta && <Alerta {...alerta} onOk={() => setAlerta(null)} />}
    </div>
  );
}
